/**
 * How the Workshop orders projects: the sidebar, the Projects page and every
 * project list (Move to, the picker) run the same order. Last activity is the
 * default (Emad, 2 Oct); the choice is remembered per person on this device.
 */
import { useCallback, useSyncExternalStore } from "react";
import { useAuthStore } from "@/stores/authStore";
import type { WorkshopProject } from "@/types/workshop.type";

export type ProjectSort = "activity" | "newest" | "oldest" | "name";

export const PROJECT_SORTS: { id: ProjectSort; label: string; phrase: string }[] = [
  { id: "activity", label: "Last activity", phrase: "by last activity" },
  { id: "newest", label: "Newest first", phrase: "newest first" },
  { id: "oldest", label: "Oldest first", phrase: "oldest first" },
  { id: "name", label: "A to Z", phrase: "from A to Z" },
];

const sortKey = (userId: string | null) => `ws-project-sort:${userId ?? "anon"}`;

// Mounted lists hear a change made in another one (the sidebar and the
// Projects page are both up at once).
const listeners = new Set<() => void>();
const memory = new Map<string, ProjectSort>();

function readSort(userId: string | null): ProjectSort {
  const key = sortKey(userId);
  const held = memory.get(key);
  if (held) return held;
  try {
    const value = window.localStorage.getItem(key);
    if (PROJECT_SORTS.some((s) => s.id === value)) return value as ProjectSort;
  } catch {
    // Storage may be off: the default order.
  }
  return "activity";
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const time = (iso: string) => {
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
};

export function sortProjects<T extends Pick<WorkshopProject, "name" | "createdAt" | "updatedAt">>(
  projects: T[],
  sort: ProjectSort,
): T[] {
  const list = [...projects];
  switch (sort) {
    case "newest":
      return list.sort((a, b) => time(b.createdAt) - time(a.createdAt));
    case "oldest":
      return list.sort((a, b) => time(a.createdAt) - time(b.createdAt));
    case "name":
      return list.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    default:
      return list.sort((a, b) => time(b.updatedAt) - time(a.updatedAt));
  }
}

/** The person's project order, shared by every list that shows projects. */
export function useProjectSort(): [ProjectSort, (next: ProjectSort) => void] {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const sort = useSyncExternalStore(subscribe, () => readSort(userId));
  const setSort = useCallback(
    (next: ProjectSort) => {
      memory.set(sortKey(userId), next);
      try {
        window.localStorage.setItem(sortKey(userId), next);
      } catch {
        // Not remembered past this page when storage is off.
      }
      listeners.forEach((listener) => listener());
    },
    [userId],
  );
  return [sort, setSort];
}
